import React from "react";
import { View, ScrollView, Platform } from "react-native";
import { useQuery } from "@tanstack/react-query";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { AppText } from "@/components/ui/AppText";
import { Icon } from "@/components/ui/Icon";
import { PageFooter } from "@/components/ui/PageFooter";
import { useI18n } from "@/context/I18nContext";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { vendorApi, type VendorActivityData, type ActivityEventType } from "@/lib/api/vendor";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

// ─── Query key ─────────────────────────────────────────────────────────────────

export const VENDOR_ACTIVITY_QUERY_KEY = ["vendor", "activity"] as const;

// ─── Event type config ─────────────────────────────────────────────────────────

interface EventConfig {
  titleKey: TranslationKeys;
  /**
   * i18n key for the event description.
   * Present for event types whose description is static across all applications.
   * When absent, the screen falls back to the API-provided `event.description`
   * (used by dynamic event types such as `update_required` / `update_submitted`).
   */
  descriptionKey?: TranslationKeys;
  iconName: React.ComponentProps<typeof MaterialCommunityIcons>["name"];
  /** Tailwind class for the icon circle background */
  bgClass: string;
  /** Tailwind class for the icon colour */
  iconColorClass: string;
}

const EVENT_CONFIG: Record<ActivityEventType, EventConfig> = {
  application_approved: {
    titleKey: "vendor.activity.event.applicationApproved.title",
    descriptionKey: "vendor.activity.event.applicationApproved.description",
    iconName: "check",
    bgClass: "bg-green-5",
    iconColorClass: "text-green-50",
  },
  application_rejected: {
    titleKey: "vendor.activity.event.applicationRejected.title",
    descriptionKey: "vendor.activity.event.applicationRejected.description",
    iconName: "close",
    bgClass: "bg-error-light",
    iconColorClass: "text-error",
  },
  application_received: {
    titleKey: "vendor.activity.event.applicationReceived.title",
    descriptionKey: "vendor.activity.event.applicationReceived.description",
    iconName: "arrow-down-circle-outline",
    bgClass: "bg-blue-10",
    iconColorClass: "text-blue-50",
  },
  update_required: {
    // Description is dynamic (e.g. "2 fields need your attention") — provided by the API
    titleKey: "vendor.activity.event.updateRequired.title",
    iconName: "autorenew",
    bgClass: "bg-orange-light",
    iconColorClass: "text-orange",
  },
  update_submitted: {
    // Description is dynamic (e.g. "Your update to Eco Certifications…") — provided by the API
    titleKey: "vendor.activity.event.updateSubmitted.title",
    iconName: "file-check-outline",
    bgClass: "bg-blue-5",
    iconColorClass: "text-blue-50",
  },
};

// ─── Activity event card ────────────────────────────────────────────────────────

interface ActivityEventCardProps {
  type: ActivityEventType;
  description: string;
  date: string;
  time: string;
  isWide: boolean;
  testID?: string;
}

function ActivityEventCard({
  type,
  description,
  date,
  time,
  isWide,
  testID,
}: ActivityEventCardProps) {
  const { t } = useI18n();
  const config = EVENT_CONFIG[type];

  return (
    <View
      testID={testID}
      className={`w-full bg-white border border-stroke-50 rounded-2xl flex-row items-start ${isWide ? "gap-4 p-4" : "gap-3 p-4"}`}
    >
      {/* Icon circle */}
      <View
        className={`size-8 rounded-full items-center justify-center shrink-0 overflow-hidden ${config.bgClass}`}
      >
        <Icon
          as={MaterialCommunityIcons}
          name={config.iconName}
          size={20}
          className={config.iconColorClass}
        />
      </View>

      {/* Text group */}
      <View className="items-start flex-1 gap-3">
        {/* Title + description */}
        <View className="gap-1 items-start">
          <AppText weight="semibold" className="text-base text-dark-text-50">
            {t(config.titleKey)}
          </AppText>
          <AppText weight="medium" className="text-base text-supporting-text">
            {config.descriptionKey ? t(config.descriptionKey) : description}
          </AppText>
        </View>

        {/* Timestamp row */}
        <View className="flex-row items-center gap-1.5">
          <AppText weight="medium" className="text-sm tracking-[0.14px] text-supporting-text-40">
            {date}
          </AppText>
          <View className="size-1.5 rounded-full bg-dark-text-10" />
          <AppText weight="medium" className="text-sm tracking-[0.14px] text-supporting-text-40">
            {time}
          </AppText>
        </View>
      </View>
    </View>
  );
}

// ─── Main screen ────────────────────────────────────────────────────────────────

export default function VendorActivityScreen() {
  const { t } = useI18n();
  const { top } = useSafeAreaInsets();
  const isWide = useIsWideScreen();
  const { user } = useAuth();
  const { locale } = useI18n();
  const applicationId = user?.applicationId ?? null;

  const { data } = useQuery<VendorActivityData>({
    queryKey: [...VENDOR_ACTIVITY_QUERY_KEY, applicationId, locale],
    queryFn: () => vendorApi.getActivityData(applicationId!, locale),
    enabled: !!applicationId,
  });

  const events = data?.events ?? [];

  return (
    <ScrollView
      className={`flex-1 ${isWide ? "bg-stroke-20" : "bg-white"}`}
      contentContainerStyle={{ flexGrow: 1 }}
      testID="vendor-activity"
    >
      {/* ── Desktop: white page heading ─────────────────────────────── */}
      {isWide && Platform.OS === "web" && (
        <View className="bg-white px-20 pt-6 pb-6 border-b border-stroke-50 items-start w-full">
          <AppText
            weight="bold"
            className="text-[29px] leading-[32px] tracking-[-0.29px] text-dark-text-50"
          >
            {t("vendor.activity.title")}
          </AppText>
          <AppText weight="medium" className="text-base text-supporting-text-40 mt-1">
            {t("vendor.activity.subtitle")}
          </AppText>
        </View>
      )}

      {/* ── Mobile: top title bar ──────────────────────────────────── */}
      {(!isWide || Platform.OS !== "web") && (
        <View
          className="bg-white px-page-x pb-6 items-start w-full"
          style={{ paddingTop: top + 16 }}
        >
          <AppText
            weight="semibold"
            className="text-[29px] leading-[32px] tracking-[-0.29px] text-dark-text-50"
          >
            {t("vendor.activity.title")}
          </AppText>
        </View>
      )}

      {/* ── Content area ─────────────────────────────────────────────── */}
      <View className={`gap-6 items-start w-full ${isWide ? "px-20 py-6" : "pb-4"}`}>
        {/* Activities section card */}
        <View
          testID="all-activities-section"
          className={`items-start w-full bg-white overflow-hidden ${isWide ? "rounded-lg border border-stroke-50" : "border-t border-stroke-50"}`}
        >
          {/* Section header */}
          <View
            className={cn(
              "items-start w-full",
              isWide ? "bg-stroke-40 px-10 py-4" : "bg-stroke-30 px-page-x py-3"
            )}
          >
            <AppText
              weight={isWide ? "semibold" : "bold"}
              className={`uppercase text-dark-text-50 ${isWide ? "text-base" : "text-sm tracking-[0.14px]"}`}
            >
              {t("vendor.activity.allActivities")}
            </AppText>
          </View>
          <View className="border-t border-stroke-50" />

          {/* Event list */}
          <View className={cn("items-start w-full", isWide ? "gap-6 px-10 py-8" : "gap-4 p-4")}>
            {events.map((event, i) => (
              <ActivityEventCard
                key={i}
                type={event.type}
                description={event.description}
                date={event.date}
                time={event.time}
                isWide={isWide}
                testID={`activity-event-${i}`}
              />
            ))}
          </View>
        </View>
      </View>

      <PageFooter />
    </ScrollView>
  );
}
